import React, { useState, useEffect } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  makeStyles,
  Divider,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { format } from "date-fns";
import SearchCaseForm from "../SearchCase";
import DatePickerACCB from "../DatePickerACCB";
import { createFinancialEntry, updateFinancialEntry, getCalculatedFee, getCaseDetails, getAwardParties } from "../../service";
import { parseDateString, getCaseReferenceNumber, isAllowedAmountInput } from "../../utils";
import FormError from "../FormError";
import Swal from "sweetalert2";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    maxWidth: 600,
    margin: "0 auto",
    textAlign: "left",
  },
  paper: {
    padding: "2rem",
    borderRadius: "0.625rem",
    boxShadow: "none",
    border: "1px solid #ddd",
  },
  title: {
    fontWeight: 600,
    fontSize: "1.25rem",
    marginBottom: "1.5rem",
    color: theme.palette.primary.main,
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "1rem",
    marginBottom: "1rem",
    color: TEXT.primary,
  },
  label: {
    fontWeight: 500,
    fontSize: "0.875rem",
    marginBottom: theme.spacing(1),
    color: TEXT.secondary,
    display: "block",
  },
  actionBtns: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "1rem",
    marginTop: "2rem",
  },
  selectedCaseCard: {
    padding: "1rem",
    backgroundColor: "#f5f5f5",
    borderRadius: "6px",
    border: "1px solid #e0e0e0",
    marginBottom: "1.5rem",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  feeBreakdown: {
    // marginTop: "1rem",
    padding: "1rem",
    border: "1px solid #E5E5E5",
    borderRadius: "8px",
    backgroundColor: "#FAFAFA",
  },
  feeRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: "1px solid #E5E5E5",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  feeLabel: {
    fontSize: "0.875rem",
    color: TEXT.secondary,
  },
  feeValue: {
    fontSize: "0.9rem",
    fontWeight: 600,
    color: TEXT.strong,
  },
}));

export default function FinancialEntryForm({
  profile,
  initialValues,
  mode, // "create" or "edit"
  onSubmitSuccess,
  onCancel,
}) {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);

  // Form Fields
  const [sumOfDispute, setSumOfDispute] = useState("");
  const [totalFee, setTotalFee] = useState("");
  const [feeReceived, setFeeReceived] = useState("");
  const [paidById, setPaidById] = useState("");
  const [receiptDate, setReceiptDate] = useState(null);
  const [challanNumber, setChallanNumber] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [parties, setParties] = useState([]);

  // Fee calculation state
  const [proBono, setProBono] = useState(false);
  const [calcResult, setCalcResult] = useState(null);
  const [calculating, setCalculating] = useState(false);


  // Prepopulate form on Edit mode
  useEffect(() => {
    if (mode === "edit" && initialValues) {
      setSumOfDispute(initialValues.claim_amount || "");
      setTotalFee(initialValues.total_fee || "");
      setFeeReceived(initialValues.amount_paid || "");
      setPaidById(initialValues.received_by?.id || "");
      setChallanNumber(initialValues.challan_number || "");
      setProBono(!!initialValues.probono);
      if (initialValues.receipt_date) {
        setReceiptDate(parseDateString(initialValues.receipt_date));
      }
      setSelectedCase({
        id: initialValues.dispute_id,
        tiac_custom_dispute_id: initialValues.case_number || initialValues.tiac_custom_dispute_id || initialValues.dispute_id,
        name: initialValues.case_name,
      });
    }
  }, [mode, initialValues]);

  // Load parties associated with the dispute for Paid By dropdown
  useEffect(() => {
    if (selectedCase?.id) {
      getAwardParties(selectedCase.id)
        .then((res) => {
          setParties(res.data?.parties || []);
        })
        .catch((err) => {
          console.error("Error fetching award parties:", err);
          setParties([]);
        });
    } else {
      setParties([]);
    }
  }, [selectedCase?.id]);

  const handleCaseSearchSuccess = (caseDetails) => {
    const disputeData = caseDetails?.dispute_data || caseDetails;
    if (disputeData && disputeData.id) {
      setSelectedCase({
        id: disputeData.id,
        tiac_custom_dispute_id: disputeData.tiac_custom_dispute_id,
        name: disputeData.name,
        category: disputeData.category,
        customer_type: disputeData.customer_type,
        request_ticket_number: disputeData.request_ticket_number,
      });
    }
  };

  // Debounced auto fee calculation when Sum changes
  useEffect(() => {
    const amount = parseFloat(sumOfDispute);
    if (isNaN(amount) || amount <= 0) {
      setCalcResult(null);
      return;
    }

    const handler = setTimeout(() => {
      setCalculating(true);
      getCalculatedFee(sumOfDispute, proBono)
        .then((res) => {
          setCalcResult(res.data);
          // Set Total Fee automatically (unless user chooses to override later)
          const baseFee = res.data.arbitrator_fee || 0;
          const adminExpense = res.data.administrative_expense || 0;
          const probonoFee = proBono ? res.data.probono_fee || 0 : 0;
          setTotalFee(baseFee + adminExpense + probonoFee);
          setCalculating(false);
        })
        .catch((err) => {
          console.error("Fee calculation error:", err);
          setCalculating(false);
        });
    }, 500);

    return () => clearTimeout(handler);
  }, [sumOfDispute, proBono]);

  const handleSave = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    if (!selectedCase) {
      setFieldErrors({ form: "Please select a case." });
      return;
    }
    if (!sumOfDispute || isNaN(parseFloat(sumOfDispute))) {
      setFieldErrors({ sumOfDispute: "Please enter a valid sum in dispute." });
      return;
    }
    if (!totalFee || isNaN(parseFloat(totalFee))) {
      setFieldErrors({ totalFee: "Please enter total fee." });
      return;
    }
    if (!feeReceived || isNaN(parseFloat(feeReceived))) {
      setFieldErrors({ feeReceived: "Please enter fee received." });
      return;
    }
    if (!receiptDate) {
      setFieldErrors({ receiptDate: "Please select a date of receipt." });
      return;
    }
    if (!challanNumber.trim()) {
      setFieldErrors({ challanNumber: "Please enter a challan number." });
      return;
    }

    const payload = {
      dispute_id: selectedCase.id,
      sum: parseFloat(sumOfDispute),
      fee_received: parseFloat(feeReceived),
      received_by: paidById || null,
      date_receipt: format(receiptDate, "yyyy-MM-dd"),
      chellan_number: challanNumber, // backend requires chellan_number with an 'e'
    };

    setLoading(true);
    try {
      if (mode === "create") {
        await createFinancialEntry(payload, proBono);
        Swal.fire("Success", "Financial details created successfully.", "success");
      } else {
        await updateFinancialEntry(initialValues.id, payload, proBono);
        Swal.fire("Success", "Financial details updated successfully.", "success");
      }
      if (onSubmitSuccess) onSubmitSuccess();
    } catch (err) {
      console.error("Save financial entry error:", err);
      setFieldErrors({ form: "An error occurred. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => {
    if (val === undefined || val === null) return "—";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val);
  };


  const refNo = getCaseReferenceNumber(selectedCase);

  const dropDownOptions = [...parties];
  if (paidById && !dropDownOptions.some((opt) => opt.profile_id === paidById)) {
    let fallbackLabel = "Selected Profile";
    if (initialValues?.received_by) {
      const rec = initialValues.received_by;
      if (rec.user) {
        fallbackLabel = `${rec.user.first_name || ""} ${rec.user.last_name || ""}`.trim();
      } else {
        fallbackLabel = rec.name || "Selected Profile";
      }
    }
    dropDownOptions.push({
      profile_id: paidById,
      label: fallbackLabel,
    });
  }

  return (
    <Box className={classes.root}>
      <Breadcrumbs aria-label="breadcrumb" style={{ marginBottom: 16 }}>
        <Link
          color="inherit"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            onCancel();
          }}
          style={{ cursor: "pointer" }}
        >
          Financial Dashboard
        </Link>
        <Typography color="textPrimary">
          {mode === "create" ? "New Financial Entry" : "Edit Financial Entry"}
        </Typography>
      </Breadcrumbs>
      <Paper className={classes.paper}>
        <Typography className={classes.title}>
          {mode === "create" ? "New Financial Entry" : "Edit Financial Entry"}
        </Typography>

        {/* Case Selection */}
        {mode === "create" && !selectedCase && (
          <Box mb={3}>
            <Typography className={classes.sectionTitle}>Select Case</Typography>
            <SearchCaseForm
              classes={{
                paper: "",
                button: "",
              }}
              handleSearchSuccess={handleCaseSearchSuccess}
            />
          </Box>
        )}

        {selectedCase && (
          <Box className={classes.selectedCaseCard}>
            <Box>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                Case No: {selectedCase.tiac_custom_dispute_id}
              </Typography>
              {refNo && (
                <Typography variant="body2" color="textSecondary" style={{ overflowWrap: "anywhere" }}>
                  {refNo.label}: {refNo.value}
                </Typography>
              )}
              <Typography variant="body2" color="textSecondary">
                Title: {selectedCase.name}
              </Typography>
            </Box>
            {mode === "create" && (
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => setSelectedCase(null)}
              >
                Change
              </Button>
            )}
          </Box>
        )}

        {selectedCase && (
          <form onSubmit={handleSave}>
            <Grid container spacing={3}>
              {/* Sum in Dispute */}
              <Grid item xs={12} sm={6}>
                <Typography className={classes.label}>Sum in Dispute (INR)</Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  type="number"
                  required
                  error={!!fieldErrors.sumOfDispute}
                  helperText={fieldErrors.sumOfDispute}
                  value={sumOfDispute}
                  onChange={(e) => {
                    if (isAllowedAmountInput(e.target.value)) setSumOfDispute(e.target.value);
                  }}
                />
              </Grid>

              {/* Total Fee */}
              <Grid item xs={12} sm={6}>
                <Typography className={classes.label}>Total Fee (INR)</Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  type="number"
                  required
                  error={!!fieldErrors.totalFee}
                  helperText={fieldErrors.totalFee}
                  value={totalFee}
                  onChange={(e) => {
                    if (isAllowedAmountInput(e.target.value)) setTotalFee(e.target.value);
                  }}
                  InputProps={{
                    endAdornment: calculating && <CircularProgress size={16} />,
                  }}
                />
              </Grid>

              {/* Pro Bono Checkbox (Conditionally visible if dispute sum is less than 500k) */}
              {parseFloat(sumOfDispute) <= 500000 && (
                <Grid item xs={12} style={{ paddingTop: 0, paddingBottom: 0 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={proBono}
                        onChange={(e) => setProBono(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Pro-Bono"
                  />
                </Grid>
              )}

              {/* Fee Breakdown display */}
              {calcResult && (
                <Grid item xs={12}>
                  <Box className={classes.feeBreakdown}>
                    <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
                      Fee Calculation Breakdown
                    </Typography>
                    <Box className={classes.feeRow}>
                      <Typography className={classes.feeLabel}>Administrative Expenses</Typography>
                      <Typography className={classes.feeValue}>
                        {formatCurrency(calcResult.administrative_expense)}
                      </Typography>
                    </Box>
                    <Box className={classes.feeRow}>
                      <Typography className={classes.feeLabel}>Arbitrator Fee</Typography>
                      <Typography className={classes.feeValue}>
                        {formatCurrency(calcResult.arbitrator_fee)}
                      </Typography>
                    </Box>
                    {proBono && calcResult.probono_fee !== undefined && (
                      <Box className={classes.feeRow}>
                        <Typography className={classes.feeLabel}>Pro-Bono Fee</Typography>
                        <Typography className={classes.feeValue}>
                          {formatCurrency(calcResult.probono_fee)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              )}

              {/* Fee Received */}
              <Grid item xs={12} sm={6}>
                <Typography className={classes.label}>Fee Received (INR)</Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  type="number"
                  required
                  error={!!fieldErrors.feeReceived}
                  helperText={fieldErrors.feeReceived}
                  value={feeReceived}
                  onChange={(e) => {
                    if (isAllowedAmountInput(e.target.value)) setFeeReceived(e.target.value);
                  }}
                />
              </Grid>

              {/* Paid By Dropdown */}
              <Grid item xs={12} sm={6}>
                <FormControl variant="outlined" size="small" fullWidth required>
                  {/* <InputLabel id="paid-by-label">Paid By</InputLabel> */}
                  <Typography className={classes.label}>Paid By</Typography>
                  <Select
                    // labelId="paid-by-label"
                    // label="Paid By"
                    value={paidById}
                    onChange={(e) => setPaidById(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select...</em>
                    </MenuItem>
                    {dropDownOptions.map((opt) => (
                      <MenuItem key={opt.profile_id} value={opt.profile_id}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Date of Receipt */}
              <Grid item xs={12} sm={6}>
                <Typography className={classes.label}>Date of Receipt</Typography>
                <DatePickerACCB
                  // label="Date of Receipt"
                  value={receiptDate}
                  onChange={setReceiptDate}
                  required
                  maxDate={new Date()}
                  error={!!fieldErrors.receiptDate}
                  helperText={fieldErrors.receiptDate}
                />
              </Grid>

              {/* Challan Number */}
              <Grid item xs={12} sm={6}>
                <Typography className={classes.label}>Challan Number</Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  required
                  error={!!fieldErrors.challanNumber}
                  helperText={fieldErrors.challanNumber}
                  value={challanNumber}
                  onChange={(e) => setChallanNumber(e.target.value)}
                />
              </Grid>
            </Grid>

            <FormError message={fieldErrors.form} />

            {/* Form Actions */}
            <Box className={classes.actionBtns}>
              <Button variant="outlined" onClick={onCancel} disabled={loading} size="small">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                size="small"
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
}
